import { useRef } from "react";
import {
    compose,
    MiddlewareAPI,
    Dispatch,
    AnyAction,
    createStore,
    applyMiddleware,
    Store,
} from "redux";
import appReducers from "data/reducers";
// responsive
import { responsiveStoreEnhancer } from "redux-responsive";
// sagas
import createSagaMiddleware from "redux-saga";
import { localRootSaga } from "data/sagas/rootSaga";
import { userIdSelector } from "data/reducers/selectors";

import { getUserResultAction } from "data/actions";
import Constants from "data/constants";
import { userId, clonedUser } from "data/storeHelper";

// Middleware to set the user in the LocalStore
const setUserMiddleware =
    (store: MiddlewareAPI<Dispatch, any>) =>
    (next: Dispatch<AnyAction>) =>
    (action: AnyAction) => {
        // Execute here to avoid endless loop
        const result = next(action);
        const id = userId();

        // If the user is not the current user, we need to update the user in the LocalStore
        if (userIdSelector(store.getState()) !== id) {
            store.dispatch(
                getUserResultAction({
                    status: Constants.OK,
                    info: clonedUser(),
                })
            );
        }

        return result;
    };

type StoreNameProp = string;

const getStore = (storeName: StoreNameProp = "LocalStore"): Store => {
    // create the saga middleware
    const sagaMiddleware = createSagaMiddleware();

    // http://redux.js.org/
    const middlewares = [sagaMiddleware, setUserMiddleware];
    /*istanbul ignore next*/
    const composeEnhancers =
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?.({
            name: `inPoint.Web ${storeName}`,
        }) || compose;
    const store = createStore(
        appReducers,
        composeEnhancers(
            responsiveStoreEnhancer,
            applyMiddleware(...middlewares)
        )
    );

    // then run the rootSaga which forks all the others
    sagaMiddleware.run(localRootSaga);

    return store;
};

type UseLocalStore = (storeName?: StoreNameProp) => Store;

const useLocalStore: UseLocalStore = (storeName) => {
    const store = useRef<Store | null>(null);
    /* istanbul ignore else */
    if (store.current == null) {
        store.current = getStore(storeName);
    }
    return store.current;
};

export { useLocalStore, getStore };
